import { notifyAnalytics } from '@/api/analytics/notifyAnalytics'
import directus, { storage } from '@/api/clients/directus-client'
import { keycloak } from '@/main'
import { readUserPermissions, type ReadUserPermissionsOutput } from '@directus/sdk'

/**
 * API Call to check if current token exists and is valid.
 * This will authenticate the directus connection if successful.
 *
 * @returns `true` if token auth successful `false` if failed.
 */
export async function checkToken(): Promise<boolean> {
  let authenticated = false
  const realm = keycloak.realm

  if (realm) {
    await fetch('https://auth.powertrip.earth/auth/realms/' + realm + import.meta.env.VITE_SIDECAR_ENDPOINT, {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then(async (res: AuthRes) => {
        // set custom credentials to the storage
        storage.set(res.data)
        await directus
          .refresh()
          .then((data) => {
            authenticated = true
          })
          .catch((err: unknown) => {
            console.error(err)
            // The user is not authenticated
            keycloak.logout({redirectUri: window.location.origin,})
          })
      })
  }
  return authenticated
}

/**
 * API Call to logout the user.
 * This will invalidate the directus connection if successful and is a fire and forget style function.
 */
export async function logout() {
  try {
    await directus.logout()
    storage.clear() //remove directus tokens
    await keycloak.logout({
      redirectUri: window.location.origin,
    })
  } catch (error) {
    notifyAnalytics({ type: 'log_error', data: error })
  }
}

interface AuthRes {
  data: Data
}

interface Data {
  access_token: string
  refresh_token: string
  expires: number
}

export async function fetchPermissions(): Promise<ReadUserPermissionsOutput | undefined> {
  try {
    // check if user is logged in
    const authData = await storage.get()
    // Guard clause: exit if no auth data available to prevent 401 error
    if (Object.keys(authData).length === 0) return undefined
    const res = await directus.request(readUserPermissions())
    return res
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      console.error('Error fetching permissions:', error)
    } else {
      notifyAnalytics({ type: 'log_error', data: error })
    }
  }
}
